import { ChangeEvent, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useFormContext } from 'react-hook-form'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import { getName } from 'components/Topbar/components/ProfileDropdown/utils'
import { S3FileTypeEnum } from 'constants/aws'
import { getS3FileUrl } from 'utils/images'
import styles from './profilePictureSelector.module.scss'

interface IProfilePictureSelectorProps {
  userId: number
}

const ProfilePictureSelector = ({ userId }: IProfilePictureSelectorProps) => {
  const { setValue, register } = useFormContext()
  const { userStore, authStore } = useStores()
  const fileInputRef = useRef<any>(null)
  const [selectedFile, setSelectedFile] = useState<any>()
  const [isEdit, setIsEdit] = useState(false)
  const [preview, setPreview] = useState('')
  const [selectedPicture, setSelectedPicture] = useState('')
  const { selectedUserDetail } = userStore
  const isMyAccount = userId === authStore.userDetail.id
  const userDetail = isMyAccount ? userStore.userDetail : selectedUserDetail
  const imageUrl = userDetail?.image
    ? getS3FileUrl(S3FileTypeEnum.IMAGE, authStore.activeOrgId ?? 0, userDetail.image)
    : ''
  const name = getName(userDetail)

  useEffect(() => {
    register('imageFile')
  }, [])

  useEffect(() => {
    if (!selectedFile) {
      setPreview('')
      return
    }

    const objectUrl = URL.createObjectURL(selectedFile)
    setPreview(objectUrl)

    // eslint-disable-next-line consistent-return
    return () => URL.revokeObjectURL(objectUrl)
  }, [selectedFile])

  function onSelectFile(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files || event.target.files.length === 0) {
      setSelectedFile(undefined)
      return
    }

    setSelectedFile(event.target.files[0])
    setIsEdit(true)
  }

  function handleOpenPreview(): void {
    fileInputRef?.current?.click()
  }

  function handleCancel(): void {
    setIsEdit(false)
    setPreview('')
    setSelectedPicture('')
  }

  async function handleAccept(): Promise<void> {
    if (selectedFile) {
      setValue('imageFile', selectedFile)
      setSelectedPicture(preview)
      setIsEdit(false)
      setPreview(URL.createObjectURL(selectedFile))
    }
  }

  return (
    <div className={styles.profileWrapper}>
      <Avatar
        src={userStore?.userDetail?.image || preview ? preview || selectedPicture || imageUrl : ''}
        name={name}
        className={styles.avatar}
      />
      {isEdit ? (
        <div className={cx(styles.iconEdit, styles.pointer, styles.submit)} onClick={handleAccept}>
          <Icon className={styles.iconSubmit} group="fontawesome" icon="check" />
        </div>
      ) : (
        <div className={cx(styles.iconEdit, styles.pointer)} onClick={handleOpenPreview}>
          <input type="file" ref={fileInputRef} onChange={onSelectFile} style={{ display: 'none' }} />
          <Icon group="fontawesome" icon="pen" />
        </div>
      )}

      {isEdit && (
        <div className={cx(styles.iconEdit, styles.pointer, styles.iconCancel)} onClick={handleCancel}>
          <Icon className={styles.iconSubmit} group="fontawesome" icon="times" />
        </div>
      )}
    </div>
  )
}

export default observer(ProfilePictureSelector)
